import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { formatDemoCountriesSchedulePreview, formatDemoDualCaption, getTodayYMDInTimezone } from '../../utils/dateTimeUtils';
import { getTimezoneAbbreviation, getTimezoneOptions, normalizeTimezoneForFormSelect, normalizeTimezoneForStorage } from '../../utils/timezoneUtils';
import { hasMeetingUrl, normalizeMeetingUrlInput } from '../../utils/meetingUrl';
import { demosService } from '../../services/demos';
import { teachersService } from '../../services/teachers';
import TimeSelector from '../../components/TimeSelector';
import DemoScheduleExceptionPanel from '../../components/Demos/DemoScheduleExceptionPanel';
import {
  checkDemoTimeAgainstTeacherAvailability,
  buildScheduleExceptionNote,
  formatTimeAMPM,
  timeToMinutes,
  minutesToTime,
} from '../../utils/demoTeacherAvailabilityCheck';
import { formatChildAgeForDemo } from './leadDisplayUtils';
import { formatDemoInstructorLabel } from '../../utils/demoInstructorLabel';
import { useAuth } from '../../contexts/AuthContext';

const AVAILABLE_DEMO_STATUSES = ['scheduled', 'confirmed', 'rescheduled'];

const DemoScheduleForm = ({ lead, onSuccess, onCancel, isDialog = false }) => {
  const { user } = useAuth();
  const browserTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'UTC';
    }
  }, []);
  const userTimezone = user?.timezone || browserTimezone || 'UTC';
  const defaultTimezone = normalizeTimezoneForFormSelect(lead?.timezone || userTimezone);

  const [mode, setMode] = useState('assign'); // 'assign' | 'create'
  const [availableDemos, setAvailableDemos] = useState([]);
  const [loadingDemos, setLoadingDemos] = useState(false);
  const [selectedDemoId, setSelectedDemoId] = useState(null);
  const [assigningToDemo, setAssigningToDemo] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    type: 'demo',
    scheduled_date: null,
    scheduled_time: null,
    scheduled_timezone: defaultTimezone,
    end_time: null,
    duration_minutes: 30,
    teacher_id: '',
    notes: '',
    meeting_url: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [availableTimeRanges, setAvailableTimeRanges] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [teacherInfo, setTeacherInfo] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [availabilityMeta, setAvailabilityMeta] = useState(null);
  const [scheduleExceptionReason, setScheduleExceptionReason] = useState(null);
  const [allowScheduleException, setAllowScheduleException] = useState(false);

  const fetchTeachers = useCallback(async (demoType) => {
    try {
      const response = await teachersService.getTeachersForSessionType(demoType);
      setTeachers(response.data || []);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  }, []);

  const fetchAvailableDemos = useCallback(async () => {
    if (!lead?.id) return;
    setLoadingDemos(true);
    try {
      const today = getTodayYMDInTimezone(userTimezone);
      const res = await demosService.getDemos({
        date_from: today,
        limit: 500,
        viewer_timezone: userTimezone,
      });
      const list = res?.data ?? res?.demos ?? [];
      const arr = Array.isArray(list) ? list : [];
      const future = arr.filter((d) => AVAILABLE_DEMO_STATUSES.includes(d.status));
      setAvailableDemos(future);
      setSelectedDemoId(null);
    } catch (err) {
      console.error('Error fetching available demos:', err);
      setAvailableDemos([]);
    } finally {
      setLoadingDemos(false);
    }
  }, [lead?.id, userTimezone]);

  useEffect(() => {
    if (mode === 'assign') {
      fetchAvailableDemos();
    }
  }, [mode, fetchAvailableDemos]);

  useEffect(() => {
    const defaultName = lead ? `Demo for ${lead.first_name} ${lead.last_name}` : '';
    setFormData({
      name: defaultName,
      type: 'demo',
      scheduled_date: null,
      scheduled_time: '',
      scheduled_timezone: defaultTimezone,
      end_time: '',
      duration_minutes: 30,
      teacher_id: '',
      notes: '',
      meeting_url: '',
    });
    setError('');
    setSelectedDate(null);
    setAvailableTimeRanges([]);
    setTeacherInfo(null);
    setSelectedTeacher(null);
    setAvailabilityMeta(null);
    setScheduleExceptionReason(null);
    setAllowScheduleException(false);
    fetchTeachers('demo'); // Default to demo type
  }, [fetchTeachers, lead, defaultTimezone]);

  const fetchAvailableTimeRanges = useCallback(async (teacherId, date) => {
    if (!teacherId || !date) return;

    try {
      setLoadingSlots(true);
      // Format date to YYYY-MM-DD string format
      const formattedDate = dayjs(date).format('YYYY-MM-DD');
      const response = await demosService.getAvailableSlots(teacherId, formattedDate, normalizeTimezoneForStorage(userTimezone) || userTimezone);
      setAvailableTimeRanges(response.data.available_time_ranges || []);
      setAvailabilityMeta({
        teacher_availability: response.data.teacher_availability,
        booked_batches: response.data.booked_batches || [],
        booked_demos: response.data.booked_demos || [],
        skip_work_hours: !!response.data.skip_work_hours,
      });
      setTeacherInfo({
        name: response.data.teacher_name,
        timezone: response.data.teacher_timezone,
        studentTimezone: response.data.student_timezone,
        availability: response.data.teacher_availability
      });
    } catch (error) {
      console.error('Error fetching available time ranges:', error);
      setAvailableTimeRanges([]);
      setTeacherInfo(null);
      setAvailabilityMeta(null);
    } finally {
      setLoadingSlots(false);
    }
  }, [userTimezone]);

  const dayLabel = selectedDate ? selectedDate.format('dddd') : 'this day';

  const recomputeScheduleException = useCallback(
    (time, durationMin) => {
      const startTime = time ?? formData.scheduled_time;
      const duration = durationMin ?? formData.duration_minutes;
      if (!startTime || !formData.teacher_id || !selectedDate) {
        setScheduleExceptionReason(null);
        setAllowScheduleException(false);
        return;
      }
      const result = checkDemoTimeAgainstTeacherAvailability({
        startTime,
        durationMinutes: duration,
        availableTimeRanges,
        teacherAvailability: availabilityMeta?.teacher_availability,
        bookedBatches: availabilityMeta?.booked_batches || [],
        bookedDemos: availabilityMeta?.booked_demos || [],
        dayLabel,
        skipWorkHours: !!availabilityMeta?.skip_work_hours,
      });
      setScheduleExceptionReason(result.exceptionReason);
      if (result.ok) setAllowScheduleException(false);
    },
    [
      formData.scheduled_time,
      formData.duration_minutes,
      formData.teacher_id,
      selectedDate,
      availableTimeRanges,
      availabilityMeta,
      dayLabel,
    ]
  );

  useEffect(() => {
    recomputeScheduleException();
  }, [recomputeScheduleException]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setFormData(prev => ({ ...prev, scheduled_date: date }));
    
    if (formData.teacher_id && date) {
      fetchAvailableTimeRanges(formData.teacher_id, date);
    }
  };

  const handleTeacherChange = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    setSelectedTeacher(teacher);
    setFormData(prev => ({ ...prev, teacher_id: teacherId }));
    
    if (selectedDate && teacherId) {
      fetchAvailableTimeRanges(teacherId, selectedDate);
    }
  };

  // Helper function to format time in AM/PM format — use shared util

  const handleTimeChange = (time) => {
    setError('');

    if (!time) {
      setFormData((prev) => ({
        ...prev,
        scheduled_time: '',
        end_time: '',
      }));
      setScheduleExceptionReason(null);
      setAllowScheduleException(false);
      return;
    }

    const selectedTimeMinutes = timeToMinutes(time);
    const endTimeMinutes = selectedTimeMinutes + formData.duration_minutes;
    const endTime = minutesToTime(endTimeMinutes);

    setFormData((prev) => ({
      ...prev,
      scheduled_time: time,
      end_time: endTime,
    }));
    recomputeScheduleException(time, formData.duration_minutes);
  };

  const calculateMatchScore = (teacher, student) => {
    if (!teacher || !student) return 0;
    
    let score = 0;
    let totalCriteria = 0;
    
    // Timezone compatibility (higher weight)
    if (teacher.timezone === student.timezone) {
      score += 2;
    }
    totalCriteria += 2;
    
    // Special needs training
    if (student.student_type !== 'normal' && teacher.is_trained_special_needs) {
      score += 1;
    }
    totalCriteria += 1;
    
    // Workload consideration
    if (teacher.active_batches < 3) {
      score += 1;
    }
    totalCriteria += 1;
    
    return (score / totalCriteria) * 100;
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // If type changes, refetch teachers and clear teacher selection
      if (field === 'type') {
        fetchTeachers(value);
        newData.teacher_id = '';
        setSelectedTeacher(null);
        setAvailableTimeRanges([]);
        setTeacherInfo(null);
        setAvailabilityMeta(null);
        setScheduleExceptionReason(null);
        setAllowScheduleException(false);
      }
      
      // Recalculate end time if duration or start time changes
      if ((field === 'duration_minutes' || field === 'scheduled_time') && newData.scheduled_time) {
        const startTimeMinutes = timeToMinutes(newData.scheduled_time);
        const endTimeMinutes = startTimeMinutes + newData.duration_minutes;
        newData.end_time = minutesToTime(endTimeMinutes);
      } else if (field === 'scheduled_time' && !newData.scheduled_time) {
        // Clear end time if start time is cleared
        newData.end_time = '';
      }
      
      return newData;
    });
    if (field === 'duration_minutes') {
      setTimeout(() => recomputeScheduleException(formData.scheduled_time, value), 0);
    }
  };



  const handleAssignToDemo = async () => {
    if (!selectedDemoId || !lead?.id) return;
    setAssigningToDemo(true);
    setError('');
    try {
      await demosService.addLeadToDemo(selectedDemoId, lead.id);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to assign lead to demo');
    } finally {
      setAssigningToDemo(false);
    }
  };

  const formatDemoDateTime = (d) => formatDemoDualCaption(d, userTimezone);

  const handleSubmit = async () => {
    if (!formData.name || !formData.type || !formData.scheduled_date || !formData.scheduled_time || !lead?.id) {
      setError('Please fill in all required fields');
      return;
    }
    if (!hasMeetingUrl(formData.meeting_url)) {
      setError('Demo join link is required');
      return;
    }

    if (formData.teacher_id && scheduleExceptionReason && !allowScheduleException) {
      setError('Confirm the schedule exception to continue, or choose a time within availability.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      let notes = formData.notes?.trim() || '';
      if (scheduleExceptionReason && allowScheduleException) {
        notes = buildScheduleExceptionNote(scheduleExceptionReason, notes);
      }

      if (!lead.country) {
        setError('Lead country is required to create a demo');
        return;
      }

      const demoData = {
        name: formData.name.trim(),
        type: formData.type,
        lead_ids: [lead.id], // Array of lead IDs
        scheduled_date: dayjs(formData.scheduled_date).format('YYYY-MM-DD'),
        scheduled_time: formData.scheduled_time + ':00', // Add seconds to time string
        scheduled_timezone: normalizeTimezoneForStorage(formData.scheduled_timezone || defaultTimezone) || 'UTC',
        duration_minutes: formData.duration_minutes,
        countries: [lead.country],
        channel: 'crm_only',
        capacity: 12,
        ...(formData.teacher_id && { teacher_id: formData.teacher_id }),
        ...(notes && { notes }),
        meeting_url: normalizeMeetingUrlInput(formData.meeting_url),
      };

      await demosService.createDemo(demoData);
      onSuccess();
    } catch (error) {
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Handle validation errors from backend
        const errorMessages = error.response.data.errors.join(', ');
        setError(errorMessages);
      } else {
        setError(error.response?.data?.message || error.response?.data?.error || 'Failed to schedule demo');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onCancel();
    }
  };

  const enteredTimezone = formData.scheduled_timezone || defaultTimezone;
  const enteredAbbr = getTimezoneAbbreviation(enteredTimezone);
  const countryPreview = useMemo(
    () => formatDemoCountriesSchedulePreview(
      selectedDate || formData.scheduled_date,
      formData.scheduled_time,
      enteredTimezone,
      lead?.country ? [lead.country] : []
    ),
    [selectedDate, formData.scheduled_date, formData.scheduled_time, enteredTimezone, lead?.country]
  );

  const assignModeContent = (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      <Typography variant="body1" sx={{ mb: 2 }}>
        Assign <strong>{lead?.first_name} {lead?.last_name}</strong> to an existing demo or create a new one.
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Available demos (from today onward, timezone: {userTimezone})
        </Typography>
        {loadingDemos ? (
          <Box display="flex" alignItems="center" gap={1} py={2}>
            <CircularProgress size={24} />
            <Typography variant="body2">Loading demos...</Typography>
          </Box>
        ) : availableDemos.length === 0 ? (
          <Alert severity="info">
            No upcoming demos found. Click &quot;Create new demo&quot; to schedule one.
          </Alert>
        ) : (
          <List dense sx={{ maxHeight: 320, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {availableDemos.map((d) => (
              <ListItemButton
                key={d.id}
                selected={selectedDemoId === d.id}
                onClick={() => setSelectedDemoId(d.id)}
              >
                <ListItemText
                  primary={d.name}
                  secondary={
                    <>
                      {formatDemoDateTime(d)} · {d.duration_minutes || 30} min ·{' '}
                      {d.teacher_first_name || d.teacher_last_name
                        ? `${d.teacher_first_name || ''} ${d.teacher_last_name || ''}`.trim()
                        : 'No teacher'} · {d.lead_count ?? 0} lead(s)
                    </>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    </>
  );

  const formContent = (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Schedule Demo for: {lead?.first_name} {lead?.last_name}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label="Demo Name *"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Enter demo name"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Demo Type *</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) => handleChange('type', e.target.value)}
              label="Demo Type *"
            >
              <MenuItem value="demo">Demo</MenuItem>
              <MenuItem value="trial">Trial</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          {/* Student Information Section */}
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 1, 
            mb: 2, 
            p: 1.5, 
            backgroundColor: '#f5f5f5', 
            borderRadius: 1 
          }}>
            <Chip label={`Age: ${formatChildAgeForDemo(lead)}`} size="small" variant="outlined" />
            <Chip label={`Type: ${lead?.student_type || 'N/A'}`} size="small" variant="outlined" />
            <Chip label={`Class: ${lead?.class_type_preference || 'N/A'}`} size="small" variant="outlined" />
            <Chip label={`Level: ${lead?.learning_level || 'N/A'}`} size="small" variant="outlined" />
            <Chip label={`Timezone: ${lead?.timezone || 'N/A'}`} size="small" variant="outlined" color="info" />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Select Teacher *</InputLabel>
            <Select
              value={formData.teacher_id}
              onChange={(e) => handleTeacherChange(e.target.value)}
              label="Select Teacher *"
            >
              {teachers.map((teacher) => (
                <MenuItem key={teacher.id} value={teacher.id}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {formatDemoInstructorLabel(teacher)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {teacher.country} • {teacher.active_batches || 0} active batches
                      {teacher.is_trained_special_needs && ' • Special Needs Trained'}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Teacher-Student Match Indicator */}
          {selectedTeacher && lead && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              {(() => {
                const matchScore = calculateMatchScore(selectedTeacher, lead);
                const getMatchColor = (score) => {
                  if (score >= 75) return 'success';
                  if (score >= 50) return 'warning';
                  return 'error';
                };
                const getMatchText = (score) => {
                  if (score >= 75) return 'Excellent Match';
                  if (score >= 50) return 'Good Match';
                  return 'Fair Match';
                };
                
                return (
                  <>
                    <Chip
                      label={`${getMatchText(matchScore)} (${Math.round(matchScore)}%)`}
                      color={getMatchColor(matchScore)}
                      size="small"
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary">
                      💡 Consider timezone and learning level compatibility
                    </Typography>
                  </>
                );
              })()}
            </Box>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Duration</InputLabel>
            <Select
              value={formData.duration_minutes}
              onChange={(e) => handleChange('duration_minutes', e.target.value)}
              label="Duration"
            >
              <MenuItem value={15}>15 minutes</MenuItem>
              <MenuItem value={30}>30 minutes</MenuItem>
              <MenuItem value={45}>45 minutes</MenuItem>
              <MenuItem value={60}>1 hour</MenuItem>
              <MenuItem value={90}>1.5 hours</MenuItem>
              <MenuItem value={120}>2 hours</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Demo Date *"
              value={selectedDate}
              onChange={handleDateChange}
              minDate={dayjs().subtract(10, 'day')}
              slots={{
                textField: (params) => <TextField {...params} fullWidth required />
              }}
            />
          </LocalizationProvider>
        </Grid>

        {formData.teacher_id && selectedDate && (
          <Grid item xs={12}>
            {loadingSlots ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading available time ranges...</Typography>
              </Box>
            ) : availableTimeRanges.length > 0 ? (
              <Box sx={{ 
                border: '1px solid #e0e0e0', 
                borderRadius: 2, 
                p: 2, 
                backgroundColor: '#fafafa',
                mb: 2 
              }}>
                {/* Timezone Info Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Available Times for {teacherInfo?.name}
                  </Typography>
                  {teacherInfo?.studentTimezone && teacherInfo?.studentTimezone !== teacherInfo?.timezone && (
                    <Chip 
                      label={`Your timezone: ${teacherInfo.studentTimezone}`} 
                      size="small" 
                      color="info" 
                      variant="outlined"
                    />
                  )}
                </Box>

                {/* Available Time Ranges */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {availableTimeRanges.map((range, index) => (
                    <Chip
                      key={index}
                      label={`${formatTimeAMPM(range.start_time)} - ${formatTimeAMPM(range.end_time)} (${range.duration_minutes} min)`}
                      variant="outlined"
                      size="medium"
                      color="primary"
                      sx={{ fontWeight: 'bold' }}
                    />
                  ))}
                </Box>

                {/* Timezone Conversion Info */}
                {teacherInfo?.studentTimezone && teacherInfo?.studentTimezone !== teacherInfo?.timezone && (
                  <Box sx={{ 
                    backgroundColor: '#e3f2fd', 
                    p: 1.5, 
                    borderRadius: 1, 
                    border: '1px solid #bbdefb' 
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      <strong>Timezone Note:</strong> Times converted from {teacherInfo.timezone} to {teacherInfo.studentTimezone} for your convenience
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No available time ranges for the selected date. You can still schedule manually, but please verify teacher availability.
              </Alert>
            )}
            
            {/* Always show time selector, even if no slots available */}
            <Box sx={{ 
              border: '1px solid #e0e0e0', 
              borderRadius: 2, 
              p: 2, 
              backgroundColor: '#fff',
              mt: 2 
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Schedule Your Demo
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Type the time on the clock this family uses. We convert it for you.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TimeSelector
                    label={`Start Time * (${enteredAbbr})`}
                    value={formData.scheduled_time}
                    onChange={handleTimeChange}
                    disabled={!formData.teacher_id || !selectedDate}
                    error={!!scheduleExceptionReason && !allowScheduleException}
                    helperText={
                      scheduleExceptionReason
                        ? 'Outside normal availability — confirm exception below to continue'
                        : availableTimeRanges.length === 0
                          ? 'No free slots detected — you may still schedule with an exception if needed'
                          : 'Pick a time within the available ranges, or confirm an exception'
                    }
                  />
                  <DemoScheduleExceptionPanel
                    reason={scheduleExceptionReason}
                    allowOverride={allowScheduleException}
                    onAllowOverrideChange={setAllowScheduleException}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Time"
                    value={formData.end_time ? formatTimeAMPM(formData.end_time) : ''}
                    InputProps={{ readOnly: true }}
                    helperText={`Auto-calculated: ${formData.duration_minutes} minutes duration`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="demo-schedule-timezone-label">This time is on which clock?</InputLabel>
                    <Select
                      labelId="demo-schedule-timezone-label"
                      value={enteredTimezone}
                      label="This time is on which clock?"
                      onChange={(e) => handleChange('scheduled_timezone', e.target.value)}
                    >
                      {getTimezoneOptions().map((tz) => (
                        <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      If the demo is 4pm in New York, choose US Eastern and enter 4:00. No conversion to India time.
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                    {countryPreview ? (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          You entered {countryPreview.entered} ({enteredAbbr}).
                        </Typography>
                        {(countryPreview.countries || []).map((row) => (
                          <Typography key={row.country} variant="body2" sx={{ mt: 0.75 }}>
                            <strong>{row.country} families see:</strong> {row.line}
                          </Typography>
                        ))}
                      </>
                    ) : (
                      <Typography variant="body2">
                        Pick a date and time. We will show what this family&apos;s country will see.
                      </Typography>
                    )}
                  </Alert>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Demo join link"
            placeholder="https://zoom.us/j/…"
            value={formData.meeting_url}
            onChange={(e) => handleChange('meeting_url', e.target.value)}
            required
            error={!hasMeetingUrl(formData.meeting_url)}
            helperText="Required. Sent to the landing page when this demo is published there"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notes (Optional)"
            multiline
            rows={3}
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Any special notes for this demo (optional)..."
          />
        </Grid>
      </Grid>
    </>
  );

  const formActions = (
    <>
      {mode === 'assign' ? (
        <>
          <Button onClick={handleClose} disabled={assigningToDemo}>
            Cancel
          </Button>
          <Button
            onClick={() => setMode('create')}
            variant="outlined"
          >
            Create new demo
          </Button>
          <Button
            onClick={handleAssignToDemo}
            variant="contained"
            disabled={!selectedDemoId || assigningToDemo}
            startIcon={assigningToDemo ? <CircularProgress size={20} /> : null}
          >
            {assigningToDemo ? 'Assigning...' : 'Assign to selected demo'}
          </Button>
        </>
      ) : (
        <>
          <Button onClick={() => { setMode('assign'); setError(''); }} disabled={loading}>
            Back
          </Button>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              loading ||
              !hasMeetingUrl(formData.meeting_url) ||
              (formData.teacher_id && scheduleExceptionReason && !allowScheduleException)
            }
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Scheduling...' : 'Schedule Demo'}
          </Button>
        </>
      )}
    </>
  );

  const mainContent = mode === 'assign' ? assignModeContent : formContent;

  if (isDialog) {
    return (
      <Dialog open={true} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Schedule Demo</DialogTitle>
        <DialogContent>
          {mainContent}
        </DialogContent>
        <DialogActions>
          {formActions}
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      {mainContent}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        {formActions}
      </Box>
    </>
  );
};

export default DemoScheduleForm;
